import { getAccessToken, renewAccess } from '../api/client.ts'
import type { ServerEvent } from '../api/types.ts'

/**
 * Постоянное соединение с сервером.
 *
 * Мобильная сеть рвёт соединение постоянно: метро, лифт, переключение
 * с Wi-Fi на сотовую. Поэтому разрыв — норма, а не ошибка: соединение
 * восстанавливается само, с нарастающей паузой, чтобы при лежащем сервере
 * не долбить его сотней попыток в минуту.
 */

type Handler = (event: ServerEvent) => void

const handlers = new Set<Handler>()

let socket: WebSocket | null = null
let attempt = 0
let reconnectTimer: number | undefined
let pingTimer: number | undefined
let closedByUs = false
/** Когда соединение открылось: нарастающую паузу сбрасывает только удачная связь */
let openedAt = 0
/** Когда в последний раз что-нибудь пришло от сервера */
let heardAt = 0
/** Что сделать, когда связь восстановилась после перерыва */
let onReconnect: (() => void) | null = null

/** Паузы между попытками: 1, 2, 4, 8, 15, 30 секунд — дальше каждые 30. */
const backoff = (n: number): number => Math.min(1000 * 2 ** n, 30_000)

export const onServerEvent = (handler: Handler): (() => void) => {
  handlers.add(handler)
  return () => handlers.delete(handler)
}

const dispatch = (event: ServerEvent) => {
  for (const handler of handlers) handler(event)
}

export function connectSocket(): void {
  const token = getAccessToken()
  if (!token) return
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return
  }

  closedByUs = false

  const protocol = location.protocol === 'https:' ? 'wss' : 'ws'
  socket = new WebSocket(`${protocol}://${location.host}/ws?token=${encodeURIComponent(token)}`)

  socket.addEventListener('open', () => {
    /**
     * Паузу сбрасываем не здесь.
     *
     * Сервер закрывает соединение с протухшим ключом уже ПОСЛЕ того,
     * как оно открылось. Сброс по факту открытия превращал нарастающую
     * паузу в ровную секунду — и клиент с просроченным ключом стучался
     * в сервер раз в секунду сутками. Видно это было только в журнале
     * сервера: на экране ничего не происходило.
     */
    const wasDown = openedAt === 0 && heardAt !== 0
    openedAt = Date.now()
    heardAt = Date.now()
    resendFocus()

    /**
     * Сигнал раз в 25 секунд — и проверка, что на него отвечают.
     *
     * Держать соединение живым мало: прокси и мобильные сети рвут его
     * молча, и браузер об этом часто не сообщает вовсе. Соединение
     * остаётся «открытым», сообщения в него уходят в никуда, а сервер
     * считает, что человек на связи, и не шлёт ему уведомлений.
     * Отличить такое можно только по тишине в ответ.
     */
    pingTimer = window.setInterval(() => {
      if (socket?.readyState !== WebSocket.OPEN) return

      if (Date.now() - heardAt > 70_000) {
        // Две попытки без единого звука в ответ — соединение мертво,
        // сколько бы браузер ни считал его открытым
        socket.close()
        return
      }

      socket.send(JSON.stringify({ type: 'ping' }))
    }, 25_000)

    // Пока нас не было, могли прийти сообщения: события за время
    // обрыва не догоняют, их надо забрать самим
    if (wasDown) onReconnect?.()
  })

  socket.addEventListener('message', (event) => {
    heardAt = Date.now()
    try {
      dispatch(JSON.parse(event.data as string) as ServerEvent)
    } catch {
      /* мусор игнорируем */
    }
  })

  socket.addEventListener('close', (event) => {
    window.clearInterval(pingTimer)
    socket = null
    if (closedByUs) return

    // Соединение продержалось — значит, связь есть и считать попытки заново
    if (openedAt && Date.now() - openedAt > 10_000) attempt = 0
    openedAt = 0

    /**
     * 1008 — «ключ недействителен». Обновляем его и пробуем снова:
     * ключ живёт минуты, а вкладку держат открытой днями.
     *
     * Но не бесконечно: если и с новым ключом не пускают, дело не в
     * ключе, и дальше идём обычной нарастающей паузой.
     */
    if (event.code === 1008 && attempt < 3) {
      void renewAccess().then((renewed) => {
        if (!renewed || closedByUs) return
        reconnectTimer = window.setTimeout(connectSocket, 500)
      })
      attempt += 1
      return
    }

    reconnectTimer = window.setTimeout(connectSocket, backoff(attempt))
    attempt += 1
  })

  socket.addEventListener('error', () => socket?.close())
}

/**
 * Что сделать после восстановления связи.
 *
 * События, пришедшие за время обрыва, никто не хранит: сервер шлёт их
 * в никуда и забывает. Значит, после возвращения пропущенное нужно
 * забрать самим — иначе сообщение появится только тогда, когда человек
 * вручную откроет переписку заново.
 */
export function setReconnectHandler(handler: () => void): void {
  onReconnect = handler
}

export function disconnectSocket(): void {
  closedByUs = true
  heardAt = 0
  window.clearTimeout(reconnectTimer)
  window.clearInterval(pingTimer)
  socket?.close()
  socket = null
  attempt = 0
}

/**
 * Знакомство собеседников перед звонком.
 *
 * Идёт по тому же соединению, что и всё остальное: заводить второе
 * ради звонков — это второй разрыв в метро и вторая очередь
 * переподключений.
 */
export function sendCallSignal(callId: string, toUserId: string, payload: unknown): void {
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ type: 'call:signal', callId, toUserId, payload }))
  }
}

export function sendSocket(event: { type: 'typing' | 'read'; chatId: string }): void {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(event))
}

/**
 * Сообщает серверу, что открыто на экране. Нужно ровно для одного:
 * чтобы телефон не звенел на сообщение, которое человек читает прямо сейчас.
 */
let lastFocus = { chatId: null as string | null, visible: true }

export function sendFocus(chatId: string | null, visible = !document.hidden): void {
  lastFocus = { chatId, visible }
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ type: 'focus', chatId, visible }))
  }
}

/** После обрыва связи сервер забыл состояние — напоминаем. */
export function resendFocus(): void {
  sendFocus(lastFocus.chatId, lastFocus.visible)
}

/**
 * Вернувшись на вкладку после сна, соединение часто оказывается мёртвым,
 * хотя браузер об этом ещё не сообщил. Проверяем и поднимаем заново.
 */
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    sendFocus(lastFocus.chatId, document.visibilityState === 'visible')
    if (document.visibilityState === 'visible' && !closedByUs) {
      const silent = heardAt !== 0 && Date.now() - heardAt > 70_000

      if (!socket || socket.readyState > WebSocket.OPEN) {
        attempt = 0
        connectSocket()
      } else if (silent && socket.readyState === WebSocket.OPEN) {
        /**
         * Открыто, но молчит.
         *
         * После сна телефона соединение чаще всего именно такое:
         * браузер считает его открытым, а на том конце давно никого.
         * Проверка «закрыто ли» такое не ловит — ловит только тишина.
         */
        attempt = 0
        socket.close()
      }
    }
  })
}
